import { Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import { SVForm } from '../../core';
import FormSubmissionButtons from '../../FormSubmissionButtons';
import ExternalAccountFormBasicInfo from './ExternalAccountFormBasicInfo';
import { useGetCompanyListForSelection } from '@/hooks/requests/useCompanies';
import { useGetCustomerListForSelection } from '@/hooks/requests/useCustomers';
import ExternalAccountFormOwnerInfo from './ExternalAccountFormOwnerInfo';
import { ExternalAccountFormValues } from '@/types/externalAccounts';
import { useAddExternalAccount } from '@/hooks/requests/useExternalAccounts';
import { MESSAGES } from '@/constants/messages';
import showToast from '@/utils/toast';
import ExternalAccountFormProof from './ExternalAcountFormProof';
import { initialExternalAccountForm } from '@/constants/externalAccounts';
import { StatusQueryParam } from '@/types/common';

type ExternalAccountFormProps = {
  defaultValues?: ExternalAccountFormValues;
  isEdit?: boolean;
  showInactiveCustomers?: boolean;
  onLeave?: () => void;
  onSuccess: (uuid: string) => void;
};

export default function ExternalAccountForm({
  defaultValues,
  isEdit = false,
  showInactiveCustomers = false,
  onLeave,
  onSuccess,
}: ExternalAccountFormProps) {
  const { addExternalAccount, isAddExternalAccountPending } =
    useAddExternalAccount();

  const formMethods = useForm<ExternalAccountFormValues>({
    defaultValues: defaultValues ? defaultValues : initialExternalAccountForm,
  });

  const [companyId, customerId] = formMethods.watch([
    'companyId',
    'customerId',
  ]);

  const { companiesList } = useGetCompanyListForSelection({}, true);
  const { customerListByName, isCustomerListPending } =
    useGetCustomerListForSelection(
      showInactiveCustomers
        ? {
            company_id: companyId,
          }
        : {
            status: StatusQueryParam.ACTIVE,
            company_id: companyId,
          },
      !!companyId,
    );
  const doesntHaveCustomers =
    customerListByName.length === 0 &&
    Boolean(companyId) &&
    !isCustomerListPending;

  async function handleSubmit(data: ExternalAccountFormValues) {
    try {
      const response = await addExternalAccount(data);
      showToast({
        type: 'success',
        data: { content: 'The external account has been added' },
      });
      onSuccess(response);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <SVForm methods={formMethods} onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <ExternalAccountFormBasicInfo
          isEdit={isEdit}
          companyId={companyId}
          companiesList={companiesList}
          customerListByName={customerListByName}
          doesntHaveCustomers={doesntHaveCustomers}
        />
        <ExternalAccountFormOwnerInfo />
        {customerId && <ExternalAccountFormProof />}
        <FormSubmissionButtons
          isLoading={isAddExternalAccountPending}
          onLeave={onLeave}
          submitButtonProps={{
            disabled: doesntHaveCustomers,
          }}
        />
      </Stack>
    </SVForm>
  );
}
